import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 17: KALA — Art Gallery
 * Style: Museum-like, minimalist art gallery, white-space heavy with matte black accents
 * Best for: Curated Indian fashion, Designer sarees, Art kurtas, Gallery-worthy pieces
 * Pure white background with matte zinc-900 accents
 */
const Template17 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);

    // Gallery Curated Edit
    const curatedEdit = [
        {
            name: 'The Weaver\'s Legacy',
            description: 'Hand-loomed silks passed down through five generations of master artisans.',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'Monsoon Palette',
            description: 'Colors drawn from rain-washed earth and twilight skies of rural India.',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
        {
            name: 'The Minimalist Drape',
            description: 'Where restraint becomes the most powerful form of expression.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=800',
        },
    ];

    // Products
    const products = [
        { id: 1, name: 'Chanderi Silk Saree', price: '\u20B97,999', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Banarasi Art Weave', price: '\u20B912,499', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Block Print Kurta', price: '\u20B94,999', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Tussar Silk Dupatta', price: '\u20B93,499', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Ikat Palazzo Set', price: '\u20B96,999', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Kalamkari Anarkali', price: '\u20B98,499', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Ajrakh Saree', price: '\u20B919,999', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Maheshwari Silk Set', price: '\u20B911,999', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Categories
    const categories = [
        { name: 'Curated Sarees', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Art Kurtas', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Gallery Pieces', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=600' },
        { name: 'Designer Edits', image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600' },
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'Every piece from KALA feels like owning a museum artifact. The craftsmanship speaks for itself — no embellishment needed.',
            name: 'Ananya Sharma',
            city: 'Mumbai',
        },
        {
            text: 'The restraint in design is what draws me in. KALA understands that true luxury whispers, it never shouts.',
            name: 'Meera Krishnan',
            city: 'Chennai',
        },
        {
            text: 'I wore the Chanderi Silk to a gallery opening and received more compliments than any artwork on the walls.',
            name: 'Priya Reddy',
            city: 'Hyderabad',
        },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email.trim()) setEmail('');
    };

    return (
        <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen bg-white">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,wght@0,400;0,600;0,700;1,400&family=Inter:wght@300;400;500&display=swap');
            `}</style>

            {/* ===== SECTION 1: WHITE-SPACE HERO ===== */}
            {/* Mobile */}
            <section className="lg:hidden bg-white">
                <div className="px-6 pt-28 pb-12 text-center">
                    <p
                        className="text-[11px] tracking-[0.4em] uppercase mb-6"
                        style={{ color: '#71717A', fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                    >
                        The Gallery of Indian Fashion
                    </p>
                    <h1
                        className="text-4xl sm:text-5xl font-normal mb-8 leading-[1.1]"
                        style={{
                            fontFamily: "'Source Serif 4', Georgia, serif",
                            color: '#18181B',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        KALA
                    </h1>
                    <p
                        className="text-sm font-light mb-10 max-w-xs mx-auto leading-relaxed"
                        style={{ color: '#71717A', fontFamily: "'Inter', sans-serif" }}
                    >
                        Where fabric becomes canvas and tradition becomes art
                    </p>
                    <div className="w-full max-w-[280px] mx-auto aspect-[3/4] overflow-hidden mb-10">
                        <img
                            src="https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600"
                            alt="Gallery piece"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <button
                        onClick={() => navigate('/template17/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 text-[13px] tracking-[0.15em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: '#18181B',
                            color: '#FFFFFF',
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 500,
                            border: 'none',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#27272A'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#18181B'; }}
                    >
                        Enter the Gallery <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop Hero */}
            <section className="hidden lg:flex items-center justify-center min-h-screen bg-white">
                <div className="max-w-[1400px] mx-auto px-8 w-full">
                    <div className="flex flex-col items-center text-center py-16">
                        <p
                            className="text-xs tracking-[0.5em] uppercase mb-8"
                            style={{ color: '#71717A', fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                        >
                            The Gallery of Indian Fashion
                        </p>
                        <h1
                            className="text-7xl xl:text-8xl font-normal mb-10"
                            style={{
                                fontFamily: "'Source Serif 4', Georgia, serif",
                                color: '#18181B',
                                letterSpacing: '0.08em',
                            }}
                        >
                            KALA
                        </h1>
                        <p
                            className="text-base font-light mb-16 max-w-md leading-relaxed"
                            style={{ color: '#71717A', fontFamily: "'Inter', sans-serif" }}
                        >
                            Where fabric becomes canvas and tradition becomes art
                        </p>
                        <div className="w-[320px] aspect-[3/4] overflow-hidden mb-16">
                            <img
                                src="https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600"
                                alt="Gallery piece"
                                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                            />
                        </div>
                        <button
                            onClick={() => navigate('/template17/shop')}
                            className="inline-flex items-center gap-3 px-10 py-4 text-[13px] tracking-[0.2em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#18181B',
                                color: '#FFFFFF',
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 500,
                                border: 'none',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#27272A'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#18181B'; }}
                        >
                            Enter the Gallery <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: CURATED EDIT (3 Gallery Cards) ===== */}
            {/* Mobile */}
            <section className="lg:hidden bg-white">
                <div className="px-6 py-16">
                    <div className="text-center mb-10">
                        <p className="text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: '#71717A', fontWeight: 300 }}>
                            Current Exhibition
                        </p>
                        <h2
                            className="text-2xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            The Curated Edit
                        </h2>
                    </div>
                    <div className="flex flex-col gap-8">
                        {curatedEdit.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template17/shop')}
                                className="cursor-pointer group"
                            >
                                <div className="aspect-[4/5] overflow-hidden mb-4">
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                </div>
                                <h3
                                    className="text-lg font-normal mb-2"
                                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                                >
                                    {item.name}
                                </h3>
                                <p className="text-sm leading-relaxed mb-2" style={{ color: '#71717A', fontWeight: 300 }}>
                                    {item.description}
                                </p>
                                <span
                                    className="text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2"
                                    style={{ color: '#18181B', fontWeight: 500 }}
                                >
                                    View Collection <ArrowRight className="w-3 h-3" />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop Curated Edit */}
            <section className="hidden lg:block py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-8">
                    <div className="text-center mb-20">
                        <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: '#71717A', fontWeight: 300 }}>
                            Current Exhibition
                        </p>
                        <h2
                            className="text-4xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            The Curated Edit
                        </h2>
                    </div>
                    <div className="grid grid-cols-3 gap-10">
                        {curatedEdit.map((item, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template17/shop')}
                                className="cursor-pointer group"
                            >
                                <div className="aspect-[4/5] overflow-hidden mb-6">
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                                    />
                                </div>
                                <h3
                                    className="text-xl font-normal mb-3"
                                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                                >
                                    {item.name}
                                </h3>
                                <p className="text-sm leading-relaxed mb-3" style={{ color: '#71717A', fontWeight: 300 }}>
                                    {item.description}
                                </p>
                                <span
                                    className="text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300"
                                    style={{ color: '#18181B', fontWeight: 500 }}
                                >
                                    View Collection <ArrowRight className="w-3.5 h-3.5" />
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: CATEGORIES (4 Minimal Text + Image) ===== */}
            {/* Mobile */}
            <section className="lg:hidden" style={{ backgroundColor: '#FAFAFA' }}>
                <div className="px-6 py-16">
                    <div className="text-center mb-10">
                        <p className="text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: '#71717A', fontWeight: 300 }}>
                            Browse
                        </p>
                        <h2
                            className="text-2xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            The Collection
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template17/shop')}
                                className="cursor-pointer group"
                            >
                                <div className="aspect-square overflow-hidden mb-3">
                                    <img
                                        src={cat.image}
                                        alt={cat.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                    />
                                </div>
                                <p
                                    className="text-sm text-center"
                                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B', fontWeight: 400 }}
                                >
                                    {cat.name}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop Categories */}
            <section className="hidden lg:block py-32" style={{ backgroundColor: '#FAFAFA' }}>
                <div className="max-w-[1400px] mx-auto px-8">
                    <div className="text-center mb-20">
                        <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: '#71717A', fontWeight: 300 }}>
                            Browse
                        </p>
                        <h2
                            className="text-4xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            The Collection
                        </h2>
                    </div>
                    <div className="grid grid-cols-4 gap-12">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template17/shop')}
                                className="cursor-pointer group text-center"
                            >
                                <div className="aspect-[3/4] overflow-hidden mb-6">
                                    <img
                                        src={cat.image}
                                        alt={cat.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                    />
                                </div>
                                <p
                                    className="text-base"
                                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B', fontWeight: 400 }}
                                >
                                    {cat.name}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCT GRID ===== */}
            {/* Mobile: 2-col */}
            <section className="lg:hidden py-16 bg-white">
                <div className="px-6">
                    <div className="text-center mb-10">
                        <p className="text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: '#71717A', fontWeight: 300 }}>
                            Selected Works
                        </p>
                        <h2
                            className="text-2xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            Gallery Pieces
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template17/shop')}
                                className="cursor-pointer group"
                            >
                                <div className="aspect-[3/4] overflow-hidden mb-3">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                    />
                                </div>
                                <h3
                                    className="text-xs font-normal mb-1 line-clamp-1"
                                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                                >
                                    {product.name}
                                </h3>
                                <p className="text-xs" style={{ color: '#18181B', fontWeight: 400 }}>{product.price}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-10">
                        <button
                            onClick={() => navigate('/template17/shop')}
                            className="inline-flex items-center gap-2 text-[13px] tracking-[0.15em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: 'transparent',
                                border: '1px solid #18181B',
                                color: '#18181B',
                                padding: '12px 32px',
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 500,
                            }}
                        >
                            View All Pieces
                        </button>
                    </div>
                </div>
            </section>

            {/* Desktop: 4-col */}
            <section className="hidden lg:block py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-8">
                    <div className="text-center mb-20">
                        <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: '#71717A', fontWeight: 300 }}>
                            Selected Works
                        </p>
                        <h2
                            className="text-4xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            Gallery Pieces
                        </h2>
                    </div>
                    <div className="grid grid-cols-4 gap-8">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template17/shop')}
                                className="cursor-pointer group"
                            >
                                <div className="aspect-[3/4] overflow-hidden mb-4">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                    />
                                </div>
                                <h3
                                    className="text-[15px] font-normal mb-1 group-hover:underline underline-offset-[3px] transition-all"
                                    style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                                >
                                    {product.name}
                                </h3>
                                <p className="text-sm" style={{ color: '#18181B', fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>{product.price}</p>
                            </div>
                        ))}
                    </div>
                    <div className="text-center mt-16">
                        <button
                            onClick={() => navigate('/template17/shop')}
                            className="inline-flex items-center gap-3 text-[13px] tracking-[0.2em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: 'transparent',
                                border: '1px solid #18181B',
                                color: '#18181B',
                                padding: '14px 40px',
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 500,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#18181B';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#18181B';
                            }}
                        >
                            View All Pieces <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: ARTIST QUOTE ===== */}
            <section className="py-20 lg:py-32" style={{ backgroundColor: '#FAFAFA' }}>
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="w-12 h-px mx-auto mb-8" style={{ backgroundColor: '#18181B' }} />
                    <blockquote
                        className="text-xl sm:text-2xl lg:text-3xl font-normal leading-relaxed mb-8"
                        style={{
                            fontFamily: "'Source Serif 4', Georgia, serif",
                            color: '#18181B',
                            fontStyle: 'italic',
                        }}
                    >
                        "Fashion is the most powerful art there is. It is movement, design, and architecture all in one. It shows the world who we are and who we'd like to be."
                    </blockquote>
                    <p className="text-sm tracking-[0.2em] uppercase" style={{ color: '#71717A', fontWeight: 300 }}>
                        — The KALA Philosophy
                    </p>
                    <div className="w-12 h-px mx-auto mt-8" style={{ backgroundColor: '#18181B' }} />
                </div>
            </section>

            {/* ===== SECTION 6: TESTIMONIALS ===== */}
            {/* Mobile */}
            <section className="lg:hidden py-16 bg-white">
                <div className="px-6">
                    <div className="text-center mb-10">
                        <p className="text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: '#71717A', fontWeight: 300 }}>
                            Collectors Speak
                        </p>
                        <h2
                            className="text-2xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            Testimonials
                        </h2>
                    </div>
                    <div
                        className="text-center"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <p
                            className="text-base leading-relaxed mb-6 min-h-[80px]"
                            style={{
                                fontFamily: "'Source Serif 4', Georgia, serif",
                                color: '#18181B',
                                fontStyle: 'italic',
                            }}
                        >
                            "{testimonials[activeTestimonial].text}"
                        </p>
                        <p className="text-sm font-medium" style={{ color: '#18181B' }}>
                            {testimonials[activeTestimonial].name}
                        </p>
                        <p className="text-xs" style={{ color: '#71717A', fontWeight: 300 }}>
                            {testimonials[activeTestimonial].city}
                        </p>
                        <div className="flex justify-center gap-2 mt-6">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className="w-2 h-2 rounded-full transition-all duration-300"
                                    style={{
                                        backgroundColor: i === activeTestimonial ? '#18181B' : '#D4D4D8',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop Testimonials */}
            <section className="hidden lg:block py-32 bg-white">
                <div className="max-w-3xl mx-auto px-8 text-center">
                    <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: '#71717A', fontWeight: 300 }}>
                        Collectors Speak
                    </p>
                    <h2
                        className="text-4xl font-normal mb-16"
                        style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                    >
                        Testimonials
                    </h2>
                    <div
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <p
                            className="text-2xl leading-relaxed mb-8 min-h-[100px]"
                            style={{
                                fontFamily: "'Source Serif 4', Georgia, serif",
                                color: '#18181B',
                                fontStyle: 'italic',
                            }}
                        >
                            "{testimonials[activeTestimonial].text}"
                        </p>
                        <div className="w-8 h-px mx-auto mb-6" style={{ backgroundColor: '#18181B' }} />
                        <p className="text-sm font-medium tracking-wider uppercase" style={{ color: '#18181B' }}>
                            {testimonials[activeTestimonial].name}
                        </p>
                        <p className="text-sm mt-1" style={{ color: '#71717A', fontWeight: 300 }}>
                            {testimonials[activeTestimonial].city}
                        </p>
                        <div className="flex justify-center gap-3 mt-10">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className="transition-all duration-300"
                                    style={{
                                        width: i === activeTestimonial ? '24px' : '8px',
                                        height: '2px',
                                        backgroundColor: i === activeTestimonial ? '#18181B' : '#D4D4D8',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ===== */}
            <section className="py-16 lg:py-24" style={{ backgroundColor: '#FAFAFA', borderTop: '1px solid #E4E4E7' }}>
                <div className="max-w-lg mx-auto px-6 text-center">
                    <h3
                        className="text-xl lg:text-2xl font-normal mb-3"
                        style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                    >
                        Join the Gallery
                    </h3>
                    <p className="text-sm mb-8" style={{ color: '#71717A', fontWeight: 300 }}>
                        Receive invitations to new exhibitions and exclusive previews.
                    </p>
                    <form onSubmit={handleSubscribe} className="kala-newsletter-form" style={{ display: 'flex', gap: '0', maxWidth: '440px', margin: '0 auto' }}>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email address"
                            required
                            style={{
                                flex: 1,
                                padding: '14px 20px',
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E4E4E7',
                                borderRight: 'none',
                                color: '#18181B',
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '14px',
                                fontWeight: 300,
                                outline: 'none',
                            }}
                        />
                        <button
                            type="submit"
                            style={{
                                padding: '14px 28px',
                                backgroundColor: '#18181B',
                                color: '#FFFFFF',
                                border: 'none',
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '13px',
                                fontWeight: 500,
                                letterSpacing: '1.5px',
                                textTransform: 'uppercase',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#27272A')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = '#18181B')}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>

            {/* Responsive Newsletter */}
            <style>{`
                @media (max-width: 480px) {
                    .kala-newsletter-form {
                        flex-direction: column !important;
                    }
                    .kala-newsletter-form input {
                        border-right: 1px solid #E4E4E7 !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default Template17;
